import React, { useEffect, useState } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { StatusBadge } from '@/components/shared/StatusBadge';
import { PaginationFooter } from '@/components/shared/PaginationFooter';
import { Plus, Search, Download, Shield, AlertTriangle, Clock, CheckCircle2, XCircle, Eye, BarChart3, List } from 'lucide-react';
import { EmptyState } from '@/components/common/EmptyState';
import { TableSkeleton } from '@/components/common/TableSkeleton';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';

import { useAuth } from '@/lib/auth';
import { incidentLocalDate } from './incidentDates';

const SEV_COLORS = { LOW: 'bg-gray-100 text-gray-700', MEDIUM: 'bg-amber-100 text-amber-800', HIGH: 'bg-red-100 text-red-800', CRITICAL: 'bg-red-600 text-white' };
const STATUS_BADGE = { DRAFT: 'secondary', SUBMITTED: 'secondary', TRIAGED: 'default', UNDER_REVIEW: 'default', RESOLVED: 'outline', CLOSED: 'outline' };

// ── Dashboard Tab ──
function DashboardTab() {
  const [stats, setStats] = useState(null);
  const [recent, setRecent] = useState([]);
  const [highPriority, setHighPriority] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    Promise.allSettled([
      api.get('/incidents/compliance/dashboard'),
      api.get('/incidents?pageSize=8'),
      api.get('/incidents?severity=HIGH&pageSize=5'),
    ]).then(([dR, rR, hR]) => {
      if (dR.status === 'fulfilled') setStats(dR.value.data.data);
      if (rR.status === 'fulfilled') setRecent(rR.value.data.items || rR.value.data.data || []);
      if (hR.status === 'fulfilled') setHighPriority(hR.value.data.items || hR.value.data.data || []);
    });
  }, []);

  const s = stats || {};
  const sevDist = s.severity_distribution || {};

  return (
    <div className="space-y-5" data-testid="incident-dashboard-tab">
      {/* Key Metrics */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Card><CardContent className="py-4 text-center">
          <AlertTriangle className="w-5 h-5 mx-auto mb-1 text-primary" />
          <p className="text-3xl font-bold">{s.total_incidents || 0}</p>
          <p className="text-xs text-muted-foreground">Total</p>
        </CardContent></Card>
        <Card><CardContent className="py-4 text-center">
          <Clock className="w-5 h-5 mx-auto mb-1 text-blue-600" />
          <p className="text-3xl font-bold">{s.open_incidents || 0}</p>
          <p className="text-xs text-muted-foreground">Open</p>
        </CardContent></Card>
        <Card className={s.high_critical_open > 0 ? 'border-red-300 bg-red-50' : ''}>
          <CardContent className="py-4 text-center">
            <AlertTriangle className={`w-5 h-5 mx-auto mb-1 ${s.high_critical_open > 0 ? 'text-red-600' : 'text-muted-foreground'}`} />
            <p className={`text-3xl font-bold ${s.high_critical_open > 0 ? 'text-red-600' : ''}`}>{s.high_critical_open || 0}</p>
            <p className="text-xs text-muted-foreground">High/Critical</p>
          </CardContent>
        </Card>
        <Card className={s.pending_ndis_notification > 0 ? 'border-amber-300 bg-amber-50' : ''}>
          <CardContent className="py-4 text-center">
            <Shield className={`w-5 h-5 mx-auto mb-1 ${s.pending_ndis_notification > 0 ? 'text-amber-600' : 'text-muted-foreground'}`} />
            <p className={`text-3xl font-bold ${s.pending_ndis_notification > 0 ? 'text-amber-600' : ''}`}>{s.pending_ndis_notification || 0}</p>
            <p className="text-xs text-muted-foreground">NDIS Pending</p>
          </CardContent>
        </Card>
        <Card className={s.overdue_actions > 0 ? 'border-red-200' : ''}>
          <CardContent className="py-4 text-center">
            <XCircle className={`w-5 h-5 mx-auto mb-1 ${s.overdue_actions > 0 ? 'text-red-500' : 'text-muted-foreground'}`} />
            <p className={`text-3xl font-bold ${s.overdue_actions > 0 ? 'text-red-500' : ''}`}>{s.overdue_actions || 0}</p>
            <p className="text-xs text-muted-foreground">Overdue Actions</p>
          </CardContent>
        </Card>
      </div>

      {/* Secondary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card><CardContent className="py-3 text-center"><p className="text-2xl font-bold">{s.this_month || 0}</p><p className="text-xs text-muted-foreground">This Month</p></CardContent></Card>
        <Card><CardContent className="py-3 text-center"><p className="text-2xl font-bold text-red-600">{s.reportable_incidents || 0}</p><p className="text-xs text-muted-foreground">Reportable</p></CardContent></Card>
        <Card><CardContent className="py-3 text-center"><p className="text-2xl font-bold text-blue-600">{s.under_investigation || 0}</p><p className="text-xs text-muted-foreground">Under Investigation</p></CardContent></Card>
        <Card><CardContent className="py-3 text-center"><p className="text-2xl font-bold">{s.pending_actions || 0}</p><p className="text-xs text-muted-foreground">Pending Actions</p></CardContent></Card>
      </div>

      {/* Severity Distribution */}
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Severity Distribution</CardTitle></CardHeader>
        <CardContent>
          <div className="flex items-end gap-4 h-28">
            {['LOW', 'MEDIUM', 'HIGH', 'CRITICAL'].map(sev => {
              const count = sevDist[sev] || 0;
              const total = Object.values(sevDist).reduce((a, b) => a + b, 0) || 1;
              const pct = Math.max((count / total) * 100, 4);
              return (
                <div key={sev} className="flex-1 flex flex-col items-center gap-1">
                  <span className="text-lg font-bold">{count}</span>
                  <div className="w-full rounded-t-md" style={{ height: `${pct}%` }}>
                    <div className={`w-full h-full rounded-t-md ${sev === 'LOW' ? 'bg-gray-300' : sev === 'MEDIUM' ? 'bg-amber-400' : sev === 'HIGH' ? 'bg-red-400' : 'bg-red-700'}`} />
                  </div>
                  <span className="text-xs text-muted-foreground">{sev}</span>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Recent + High Priority */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base">Recent Incidents</CardTitle></CardHeader>
          <CardContent className="space-y-1">
            {recent.length === 0 ? <p className="text-sm text-muted-foreground text-center py-4">No incidents</p> :
            recent.map(inc => (
              <div key={inc.id} className="flex items-center justify-between py-1.5 border-b last:border-0 cursor-pointer hover:bg-muted/30 px-2 rounded" onClick={() => navigate(`/compliance/incidents/${inc.id}`)} data-testid={`recent-${inc.id}`}>
                <div className="flex items-center gap-2 min-w-0">
                  <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${SEV_COLORS[inc.severity]}`}>{inc.severity?.[0]}</span>
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate">{inc.incident_no} — {inc.incident_type?.replace(/_/g, ' ')}</p>
                    <p className="text-xs text-muted-foreground">{inc.participant_name || '-'} · {incidentLocalDate(inc.reported_at)}</p>
                  </div>
                </div>
                <Badge variant={STATUS_BADGE[inc.status] || 'secondary'} className="text-[10px] shrink-0">{inc.status}</Badge>
              </div>
            ))}
          </CardContent>
        </Card>
        <Card className={highPriority.some(i => i.severity === 'CRITICAL') ? 'border-red-200' : ''}>
          <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-red-500" />Requiring Attention</CardTitle></CardHeader>
          <CardContent className="space-y-1">
            {highPriority.length === 0 ? <p className="text-sm text-muted-foreground text-center py-4">No high-priority incidents</p> :
            highPriority.map(inc => (
              <div key={inc.id} className="flex items-center justify-between py-1.5 border-b last:border-0 cursor-pointer hover:bg-muted/30 px-2 rounded" onClick={() => navigate(`/compliance/incidents/${inc.id}`)} data-testid={`priority-${inc.id}`}>
                <div className="flex items-center gap-2 min-w-0">
                  <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${SEV_COLORS[inc.severity]}`}>{inc.severity}</span>
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate">{inc.incident_no} — {inc.incident_type?.replace(/_/g, ' ')}</p>
                    <p className="text-xs text-muted-foreground">{inc.participant_name} · {inc.status}</p>
                  </div>
                </div>
                <Badge variant={STATUS_BADGE[inc.status] || 'secondary'} className="text-[10px] shrink-0">{inc.status}</Badge>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

// ── List Tab ──
function IncidentsListTab() {
  const [incidents, setIncidents] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [severityFilter, setSeverityFilter] = useState('all');
  const [listParams] = useSearchParams();   // NAV-1
  const fromParam = /^\d{4}-\d{2}-\d{2}$/.test(listParams.get('from') || '') ? listParams.get('from') : '';
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, pageSize: 20, sortBy: 'created_at', sortDir: 'desc' });
      if (statusFilter !== 'all') params.set('status', statusFilter);
      if (severityFilter !== 'all') params.set('severity', severityFilter);
      if (fromParam) params.set('dateFrom', fromParam);   // NAV-1: dashboard "Incidents (week)" deep link
      const res = await api.get(`/incidents?${params.toString()}`);
      setIncidents(res.data.items || res.data.data || []);
      setTotal(res.data.total || 0);
    } catch { toast.error('Failed to load incidents'); }
    finally { setLoading(false); }
  };
  useEffect(() => { fetchData(); }, [page, statusFilter, severityFilter]); // eslint-disable-line

  const filtered = incidents.filter(i => !search ||
    i.incident_no?.toLowerCase().includes(search.toLowerCase()) ||
    i.participant_name?.toLowerCase().includes(search.toLowerCase()) ||
    i.description?.toLowerCase().includes(search.toLowerCase())
  );

  const exportCSV = async () => {
    try {
      const res = await api.get('/incidents/export-csv', { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a'); a.href = url; a.download = 'incidents.csv'; a.click();
      toast.success('CSV exported');
    } catch { toast.error('Export failed'); }
  };

  return (
    <div className="space-y-4" data-testid="incidents-list-tab">
      <div className="flex flex-col gap-3 md:flex-row md:items-center">
        <div className="relative flex-1 md:w-[280px] md:flex-none">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Search incidents..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" data-testid="incident-search" />
        </div>
        <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPage(1); }}>
          <SelectTrigger className="w-[150px]"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            {['DRAFT', 'SUBMITTED', 'TRIAGED', 'UNDER_REVIEW', 'RESOLVED', 'CLOSED'].map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={severityFilter} onValueChange={(v) => { setSeverityFilter(v); setPage(1); }}>
          <SelectTrigger className="w-[140px]"><SelectValue placeholder="Severity" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Severity</SelectItem>
            {['LOW', 'MEDIUM', 'HIGH', 'CRITICAL'].map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
        <span className="text-sm text-muted-foreground">{total} incident(s)</span>
        <Button variant="outline" size="sm" onClick={exportCSV} className="ml-auto" data-testid="export-incidents"><Download className="h-4 w-4 mr-1" />CSV</Button>
      </div>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <TableSkeleton rows={6} columns={6} testid="incidents-loading" />
          ) : filtered.length === 0 ? (
            <EmptyState
              icon={Shield}
              title={search || statusFilter !== 'all' || severityFilter !== 'all' ? 'No incidents match these filters' : 'No incidents reported'}
              description={search || statusFilter !== 'all' || severityFilter !== 'all' ? 'Try clearing filters to see all incidents.' : 'When an incident is reported it will appear here for triage and follow-up.'}
              testid="incidents-empty"
            />
          ) : (
          <Table>
            <TableHeader><TableRow>
              <TableHead>No.</TableHead><TableHead>Type</TableHead><TableHead>Severity</TableHead><TableHead>Participant</TableHead><TableHead>Status</TableHead><TableHead>Reported</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {filtered.map(i => (
                <TableRow key={i.id} className="hover:bg-muted/60 cursor-pointer" onClick={() => navigate(`/compliance/incidents/${i.id}`)} data-testid={`incident-row-${i.id}`}>
                  <TableCell className="font-mono text-xs">{i.incident_no}</TableCell>
                  <TableCell><StatusBadge status={i.incident_type} /></TableCell>
                  <TableCell><StatusBadge status={i.severity} /></TableCell>
                  <TableCell className="font-medium">{i.participant_name || 'N/A'}</TableCell>
                  <TableCell><StatusBadge status={i.status} /></TableCell>
                  <TableCell className="text-xs text-muted-foreground">{incidentLocalDate(i.reported_at)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          )}
          <PaginationFooter page={page} pageSize={20} total={total} onPageChange={setPage} />
        </CardContent>
      </Card>
    </div>
  );
}

// ── Main Page ──
export default function IncidentListPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { hasPermission } = useAuth();
  const hasDashboard = hasPermission('incidents.dashboard');
  const defaultTab = hasDashboard ? (searchParams.get('tab') || 'dashboard') : 'list';

  return (
    <div className="space-y-6 animate-fade-in" data-testid="incidents-page">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold tracking-tight flex items-center gap-2"><Shield className="w-6 h-6" />Incidents</h2>
          <p className="text-muted-foreground mt-1">NDIS Incident Management & Compliance</p>
        </div>
        {hasPermission('incidents.create') && (
          <Button onClick={() => navigate('/compliance/incidents/new')} data-testid="create-incident"><Plus className="h-4 w-4 mr-2" />Report Incident</Button>
        )}
      </div>

      <Tabs defaultValue={defaultTab}>
        <TabsList>
          {hasDashboard && <TabsTrigger value="dashboard" data-testid="tab-dashboard"><BarChart3 className="w-4 h-4 mr-1.5" />Dashboard</TabsTrigger>}
          <TabsTrigger value="list" data-testid="tab-list"><List className="w-4 h-4 mr-1.5" />{hasDashboard ? 'All Incidents' : 'My Incidents'}</TabsTrigger>
        </TabsList>
        {hasDashboard && (
          <TabsContent value="dashboard" className="mt-4">
            <DashboardTab />
          </TabsContent>
        )}
        <TabsContent value="list" className="mt-4">
          <IncidentsListTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}
